import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  TextField,
  Button,
  Chip,
  InputAdornment,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CardGiftcard, Share, Group, Redeem, EmailOutlined, CheckCircle, ContentCopy, Edit, Lock, AccountBalanceWallet } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { selectCurrentUser, setCredentials } from "../../redux/slices/authSlice";
import userService from "../../services/userService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";

const REFERRAL_STATUS_LABELS = {
  pending: { label: "Pending — awaiting their first delivery", color: "default" },
  qualified: { label: "Qualified", color: "info" },
  rewarded: { label: "Rewarded", color: "success" },
  void: { label: "Voided", color: "default" },
};

// Rewarded referrals show their actual snapshotted amount (fixed forever,
// even if the admin later changes the reward); pending/qualified ones have
// no amount yet, so this shows what they'd currently earn instead of a
// bare "—" — using the live program config, not anything stored on the
// referral itself.
const formatYourReward = (referral, program) => {
  if (referral.status === "rewarded") {
    return referral.referrerRewardAmount > 0 ? `CI$${referral.referrerRewardAmount.toFixed(2)}` : "—";
  }
  if (referral.status === "pending" || referral.status === "qualified") {
    return program.rewardReferrer && program.rewardAmount > 0 ? `Expected CI$${program.rewardAmount.toFixed(2)}` : "—";
  }
  return "—";
};

const REFERRAL_CODE_PATTERN = /^[A-Z0-9-]{4,16}$/;

const TEAL = "#00a6b2";

const STEPS = [
  {
    icon: <Share />,
    title: "Share Your Link",
    description: "Send your personal referral link to friends and family shipping from the US.",
  },
  {
    icon: <Group />,
    title: "They Sign Up & Ship",
    description: "Your friend creates an account and completes their first shipment with Waypoint.",
  },
  {
    icon: <Redeem />,
    title: "You Both Get Rewarded",
    description: "Once their shipment arrives, you both get a reward applied to your next invoice — automatically.",
  },
];

// "Notify Me" captures the email server-side (userController.
// joinReferralWaitlist) so everyone who clicked it can be emailed in one
// batch once the program actually launches. The referral code is also
// real: a customer can claim their own code now (editable exactly ONCE —
// see userController.setReferralCode), ready for whenever the program
// goes live, rather than being stuck with a system-generated one later.
const CustomerReferrals = () => {
  const theme = useTheme();
  // Brighter teal in dark mode — #00838c (chosen against a white card) is
  // too close to the dark background's own color for the accent text/icons
  // below to read clearly.
  const tealText = theme.palette.mode === "dark" ? "#4DD8E3" : "#00838c";
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const [email, setEmail] = useState(user?.email || "");
  const [notified, setNotified] = useState(!!user?.referralWaitlistedAt);
  const [notifying, setNotifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const hasCode = !!user?.referralCode;
  const [editingCode, setEditingCode] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [savingCode, setSavingCode] = useState(false);
  const [codeError, setCodeError] = useState("");

  const [creditBalance, setCreditBalance] = useState(0);
  const [referrals, setReferrals] = useState([]);
  const [referredBy, setReferredBy] = useState(null);
  const [referralProgram, setReferralProgram] = useState({ rewardAmount: 0, rewardReferrer: true, rewardReferred: true });
  const [loadingSummary, setLoadingSummary] = useState(true);

  useEffect(() => {
    userService
      .getMyReferralSummary()
      .then(({ data }) => {
        setCreditBalance(data.data?.referralCreditBalance || 0);
        setReferrals(data.data?.referrals || []);
        setReferredBy(data.data?.referredBy || null);
        if (data.data?.referralProgram) setReferralProgram(data.data.referralProgram);
      })
      .catch(() => enqueueSnackbar("Failed to load your referral activity", { variant: "error" }))
      .finally(() => setLoadingSummary(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNotify = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setNotifying(true);
    try {
      const { data } = await userService.joinReferralWaitlist(trimmed);
      if (data?.data?.user) dispatch(setCredentials({ user: data.data.user }));
      setNotified(true);
      enqueueSnackbar("You're on the list — we'll email you the moment referrals go live!", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to join the waitlist", { variant: "error" });
    } finally {
      setNotifying(false);
    }
  };

  const referralLink = user?.referralCode ? `${window.location.origin}/register?ref=${user.referralCode}` : "";

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(user.referralCode);
      setCopied(true);
      enqueueSnackbar("Referral code copied", { variant: "info" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — silently ignore */
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setLinkCopied(true);
      enqueueSnackbar("Referral link copied", { variant: "info" });
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      /* clipboard unavailable — silently ignore */
    }
  };

  const handleSaveCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!REFERRAL_CODE_PATTERN.test(code)) {
      setCodeError("4-16 characters — letters, numbers, and hyphens only");
      return;
    }
    setSavingCode(true);
    setCodeError("");
    try {
      const { data } = await userService.setReferralCode(code);
      dispatch(setCredentials({ user: data.data.user }));
      setEditingCode(false);
      enqueueSnackbar("Referral code set — this can't be changed again, so it's locked in now", { variant: "success" });
    } catch (err) {
      setCodeError(err.response?.data?.message || "Failed to set referral code");
    } finally {
      setSavingCode(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <style>{`
        @keyframes wpPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(0,166,178,0.35); }
          70% { box-shadow: 0 0 0 12px rgba(0,166,178,0); }
        }
        @keyframes wpFadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes wpSpinSlow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .wp-fade-1 { animation: wpFadeUp 0.6s ease both; }
        .wp-fade-2 { animation: wpFadeUp 0.6s ease 0.12s both; }
        .wp-fade-3 { animation: wpFadeUp 0.6s ease 0.24s both; }
        .wp-fade-4 { animation: wpFadeUp 0.6s ease 0.36s both; }
      `}</style>

      <Box sx={{ maxWidth: 860, mx: "auto", width: "100%", textAlign: "center", py: { xs: 2, sm: 3 } }}>
          {/* Badge */}
          <Box className="wp-fade-1" sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Chip
              label="LIVE"
              sx={{
                bgcolor: "rgba(14,159,110,0.1)",
                color: "#0E9F6E",
                fontWeight: 800,
                letterSpacing: 1.2,
                border: "1px solid",
                borderColor: "rgba(14,159,110,0.35)",
                px: 1.5,
                py: 2,
                animation: "wpPulse 2.4s infinite",
              }}
            />
          </Box>

          {/* Icon */}
          <Box className="wp-fade-2" sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${TEAL}, #5be0ea)`,
                boxShadow: "0 12px 32px rgba(0,166,178,0.3)",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: -6,
                  borderRadius: "50%",
                  border: "2px dashed rgba(0,166,178,0.35)",
                  animation: "wpSpinSlow 14s linear infinite",
                }}
              />
              <CardGiftcard sx={{ fontSize: 44, color: "#fff" }} />
            </Box>
          </Box>

          <Typography
            className="wp-fade-2"
            variant="h3"
            fontWeight={800}
            sx={{ color: "text.primary", mb: 1.5, fontSize: { xs: 28, sm: 40 } }}
          >
            My Waypoint Referral Program
          </Typography>
          <Typography className="wp-fade-3" sx={{ color: "text.secondary", fontSize: 17, mb: 4, maxWidth: 560, mx: "auto" }}>
            Refer a friend, and once their first shipment is delivered,{" "}
            <strong style={{ color: theme.palette.text.primary }}>you'll both be rewarded</strong> on your next invoice.
          </Typography>

          {/* How it'll work */}
          <Box
            className="wp-fade-3"
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 2,
              mb: 4,
            }}
          >
            {STEPS.map((step, i) => (
              <Card
                key={step.title}
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  p: 2.5,
                  textAlign: "left",
                  transition: "transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    borderColor: "rgba(0,166,178,0.5)",
                    boxShadow: 2,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(0,166,178,0.12)",
                      color: tealText,
                      flexShrink: 0,
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography sx={{ color: "text.disabled", fontWeight: 800, fontSize: 13 }}>STEP {i + 1}</Typography>
                </Box>
                <Typography fontWeight={700} sx={{ color: "text.primary", mb: 0.5 }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </Card>
            ))}
          </Box>

          {/* Referral code — claimable now (once), locked forever after */}
          <Card
            className="wp-fade-4"
            variant="outlined"
            sx={{
              borderStyle: "dashed",
              borderColor: "rgba(0,166,178,0.4)",
              borderRadius: 3,
              p: 2,
              mb: 4,
              maxWidth: 420,
              mx: "auto",
              bgcolor: "rgba(0,166,178,0.04)",
            }}
          >
            {hasCode ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                  <Box sx={{ textAlign: "left" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Lock sx={{ fontSize: 13 }} /> Your referral code
                    </Typography>
                    <Typography sx={{ color: tealText, fontWeight: 800, fontFamily: "monospace", fontSize: 18 }}>
                      {user.referralCode}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={handleCopyCode}
                    startIcon={copied ? <CheckCircle fontSize="small" /> : <ContentCopy fontSize="small" />}
                    sx={{ flexShrink: 0 }}
                    variant="outlined"
                  >
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </Box>

                <Box sx={{ borderTop: "1px dashed rgba(0,166,178,0.3)", pt: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                    <Box sx={{ textAlign: "left", minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Share sx={{ fontSize: 13 }} /> Share this link
                      </Typography>
                      <Typography
                        noWrap
                        sx={{ color: "text.primary", fontWeight: 700, fontFamily: "monospace", fontSize: 14 }}
                      >
                        {referralLink}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      onClick={handleCopyLink}
                      startIcon={linkCopied ? <CheckCircle fontSize="small" /> : <ContentCopy fontSize="small" />}
                      sx={{ flexShrink: 0 }}
                      variant="outlined"
                    >
                      {linkCopied ? "Copied" : "Copy Link"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            ) : editingCode ? (
              <Box sx={{ textAlign: "left" }}>
                <Typography variant="caption" color="text.secondary">
                  Choose your referral code — you can only set this once
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
                  <TextField
                    size="small"
                    autoFocus
                    fullWidth
                    placeholder="e.g. WAYPOINTIMPORTS"
                    value={codeInput}
                    onChange={(e) => {
                      setCodeInput(e.target.value.toUpperCase());
                      setCodeError("");
                    }}
                    error={!!codeError}
                    sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontWeight: 700 } }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSaveCode}
                    disabled={savingCode || !codeInput.trim()}
                    sx={{ flexShrink: 0 }}
                  >
                    {savingCode ? <CircularProgress size={18} color="inherit" /> : "Save"}
                  </Button>
                </Box>
                {codeError && (
                  <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                    {codeError}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  4-16 characters — letters, numbers, and hyphens only. Locked in once saved.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                <Box sx={{ textAlign: "left" }}>
                  <Typography variant="body2" fontWeight={700} sx={{ color: "text.primary" }}>
                    Claim your referral code
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pick it now — it's editable once, then locked in for good.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit fontSize="small" />}
                  onClick={() => setEditingCode(true)}
                  sx={{ flexShrink: 0 }}
                >
                  Choose Code
                </Button>
              </Box>
            )}
          </Card>

          {/* Credit balance + referral history */}
          <Card
            className="wp-fade-4"
            variant="outlined"
            sx={{ borderRadius: 3, p: { xs: 2.5, sm: 3 }, mb: 4, textAlign: "left" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(0,166,178,0.12)",
                    color: tealText,
                  }}
                >
                  <AccountBalanceWallet />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Your referral credit
                  </Typography>
                  <Typography fontWeight={800} fontSize={22} sx={{ color: "text.primary" }}>
                    {loadingSummary ? "—" : `CI$${creditBalance.toFixed(2)}`}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 240 }}>
                Automatically applied to your next invoice
              </Typography>
            </Box>

            {referredBy && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: "rgba(0,166,178,0.06)",
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                  mb: 2,
                }}
              >
                <Group sx={{ fontSize: 18, color: tealText }} />
                <Typography variant="body2">
                  You were referred by <strong>{referredBy.firstName} {referredBy.lastName}</strong>
                </Typography>
              </Box>
            )}

            <Typography fontWeight={700} sx={{ color: "text.primary", mb: 1 }}>
              Referral History
            </Typography>
            {loadingSummary ? (
              <SkeletonLoader rows={3} />
            ) : referrals.length === 0 ? (
              <EmptyState
                icon="🤝"
                title="No referrals yet"
                description="Once a friend signs up with your code and their first shipment is delivered, it'll show up here."
              />
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Friend</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Your Reward</TableCell>
                      <TableCell>Joined</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {referrals.map((r) => {
                      const statusInfo = REFERRAL_STATUS_LABELS[r.status] || { label: r.status, color: "default" };
                      return (
                        <TableRow key={r._id}>
                          <TableCell>
                            {r.referredUser ? `${r.referredUser.firstName} ${r.referredUser.lastName}` : "—"}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={statusInfo.label} color={statusInfo.color} variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{formatYourReward(r, referralProgram)}</TableCell>
                          <TableCell sx={{ fontSize: 12.5, color: "text.secondary" }}>
                            {dayjs(r.createdAt).format("MMM D, YYYY")}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Card>

          {/* Notify me */}
          <Card
            className="wp-fade-4"
            variant="outlined"
            sx={{
              borderRadius: 3,
              p: { xs: 2.5, sm: 3 },
              maxWidth: 460,
              mx: "auto",
            }}
          >
            {notified ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 1 }}>
                <CheckCircle sx={{ color: "success.main" }} />
                <Typography fontWeight={700} color="text.primary">
                  You're on the list!
                </Typography>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleNotify}>
                <Typography fontWeight={700} color="text.primary" mb={1.5}>
                  Be first to know when it launches
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexDirection: { xs: "column", sm: "row" } }}>
                  <TextField
                    size="small"
                    fullWidth
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailOutlined fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <Button type="submit" variant="contained" disabled={notifying} sx={{ whiteSpace: "nowrap" }}>
                    {notifying ? <CircularProgress size={18} color="inherit" /> : "Notify Me"}
                  </Button>
                </Box>
              </Box>
            )}
          </Card>
      </Box>
    </Box>
  );
};

export default CustomerReferrals;
